import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, throwError } from 'rxjs';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { HTTP_REQUEST_METHOD } from '../../enums/http-request-method.enum';
import { EnvironmentPathUtilitiesService } from '../environment-path-utilities/environment-path-utilities.service';
import { EnvironmentService } from '@evorait/pace-environment';
import {
    DeviceLockAcquireRequestBody,
    EMPTY_DEVICE_LOCK,
    loadLocalLock,
    LockValue,
    saveLocalLock,
} from '../device/device.utils';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { SmartlockerEndpoints } from '../../constants/smartlocker-constants';

@Injectable({
    providedIn: 'root',
})
export class MockModeInterceptorService implements HttpInterceptor {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly environmentPathUtilities: EnvironmentPathUtilitiesService,
        private readonly environmentService: EnvironmentService,
    ) {}

    public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (req.url.includes('mock-kv-store')) {
            return next.handle(req);
        }

        const webPushSubscribeUrl = `${this.environmentService.apiBase}${ENDPOINTS.WebPushSubscribe}`;
        const iosPushNotifyUrl = `${this.environmentService.apiBase}${ENDPOINTS.IosPushNotify}`;
        const doNotIntercept = req.url === webPushSubscribeUrl || req.url === iosPushNotifyUrl;

        const deviceLockResponse = this.interceptDeviceLock(req);
        if (deviceLockResponse) {
            return deviceLockResponse;
        }

        const mockdataFolder = this.environmentPathUtilities.getMockDataPath();
        const url = req.url;
        let mockDataUrl = mockdataFolder + ENDPOINTS.MockEmptyOk + '.json';
        if (url.indexOf(ENDPOINTS.NotificationHeaders) >= 0) {
            if (url.includes('Upcoming')) {
                mockDataUrl = mockdataFolder + '/NdpHeaderSet-Upcoming.json';
            } else {
                mockDataUrl = mockdataFolder + '/NdpHeaderSet-Todays.json';
            }
        } else if (url.indexOf(ENDPOINTS.NotificationHistory) >= 0) {
            mockDataUrl = mockdataFolder + '/NotificationHistorySet.json';
        } else if (url.indexOf(ENDPOINTS.InputFieldProperties) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.InputFieldProperties + '.json';
        } else if (url.indexOf(ENDPOINTS.CseDopSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.CseDopSet + '.json';
        } else if (url.indexOf(ENDPOINTS.ConfigSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.ConfigSet + '.json';
        } else if (url.indexOf(ENDPOINTS.ValueHelpers) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.ValueHelpers + '.json';
        } else if (url.indexOf(ENDPOINTS.WhatsNew) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.WhatsNew + '.json';
        } else if (url.indexOf(ENDPOINTS.MaterialQuote) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MaterialQuote + '.json';
        } else if (url.indexOf(ENDPOINTS.CseSale) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.CseSale + '.json';
        } else if (url.indexOf(ENDPOINTS.MaterialStockSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MaterialStockSet + '.json';
        } else if (url.indexOf(ENDPOINTS.MaterialBillingIndicatorSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MaterialBillingIndicatorSet + '.json';
        } else if (url.indexOf(ENDPOINTS.MatPricing) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MatPricing + '.json';
        } else if (url.indexOf(ENDPOINTS.MatAvailabilitySet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MatAvailabilitySet + '.json';
        } else if (url.indexOf(ENDPOINTS.F4mGetListSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.F4mGetListSet + '.json';
        } else if (url.indexOf(ENDPOINTS.WorkScheduleSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.WorkScheduleSet + '.json';
        } else if (url.indexOf(ENDPOINTS.MaterialSerialNumbersGet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MaterialSerialNumbersGet + '.json';
        } else if (url.indexOf(ENDPOINTS.SoftwareLicences) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.SoftwareLicences + '.json';
        } else if (url.indexOf(ENDPOINTS.IsAlive) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.IsAlive + '.json';
        } else if (
            url.indexOf(ENDPOINTS.F4mGetXMLSet) >= 0 ||
            url.indexOf(ENDPOINTS.F4mGetCustomXMLSet) >= 0 ||
            url.indexOf(ENDPOINTS.GetSPQXMLSet) >= 0
        ) {
            // Get form id from URL to request the right mock data
            const regex = new RegExp("FormDocid eq '([a-zA-Z0-9_.-]+)'", 'g');
            const matches = regex.exec(url);
            const targetLength = 2;
            if (matches && matches.length === targetLength) {
                const formId = matches[1];
                const urlEndPoint =
                    url.indexOf(ENDPOINTS.GetSPQXMLSet) > 0 ? ENDPOINTS.GetSPQXMLSet : ENDPOINTS.F4mGetXMLSet;
                this.log.info(`OnlineOnlyMockService: request: Loading form '${matches[1]}' from available mock data`);
                mockDataUrl = mockdataFolder + urlEndPoint + '-' + formId + '.json';
            } else {
                throw new Error(`Could not load form because URL ${url} did not contain a valid FormDocId filter`);
            }
        } else if (url.indexOf(ENDPOINTS.F4mGetPrologueSet) >= 0) {
            // sample URL: https://sng.ngm-qa58.hcvpc.io/sap/opu/odata/SIE/HC_SM_NGM_58_SRV/F4M_Get_PrologueSet(ProtocolType='832',CountryKey='IT')
            const parameterMatches = url.match(/ProtocolType='([a-zA-Z0-9]+?)',CountryKey='(\w+?)'/);
            // first one is the complete match, second one the first capture group for protocol type, third one the
            // second capture group for country key
            const expectedParameterMatches = 3;
            if (parameterMatches && parameterMatches.length === expectedParameterMatches) {
                const protocolTypeIndex = 1;
                const countryKeyIndex = 2;
                mockDataUrl = `${mockdataFolder + ENDPOINTS.F4mGetPrologueSet}_${parameterMatches[protocolTypeIndex]}_${
                    parameterMatches[countryKeyIndex]
                }.json`;
            }
        } else if (url.indexOf(ENDPOINTS.F4mSubmitFormSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.F4mSubmitFormSet + '.json';
        } else if (url.indexOf(ENDPOINTS.CseStockSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.CseStockSet + '.json';
        } else if (url.indexOf(ENDPOINTS.F4mReleaseLock) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.F4mReleaseLock + '.json';
        } else if (url.indexOf(ENDPOINTS.EncryptionKey) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.EncryptionKey + '.json';
        } else if (url.indexOf(ENDPOINTS.UserProfileSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.UserProfileSet + '.json';
        } else if (url.indexOf(ENDPOINTS.AppConfigSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.AppConfigSet + '.json';
        } else if (url.indexOf(ENDPOINTS.LegacyEncryptionKey) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.LegacyEncryptionKey + '.json';
        } else if (url.indexOf(ENDPOINTS.UserMasterSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.UserMasterSet + '.json';
        } else if (url.indexOf(ENDPOINTS.MeasToolSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MeasToolSet + '.json';
        } else if (url.indexOf(ENDPOINTS.ServiceKey) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.ServiceKey + '.json';
        } else if (url.indexOf(ENDPOINTS.GlobalConfigSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.GlobalConfigSet + '.json';
        } else if (url.indexOf(ENDPOINTS.GetEquipmentsAndInterims) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.GetEquipmentsAndInterims + '.json';
        } else if (url.indexOf(ENDPOINTS.RefreshXML) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.RefreshXML + '.json';
        } else if (url.indexOf(ENDPOINTS.GetDocBoxListSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.GetDocBoxListSet + '.json';
        } else if (url.indexOf(ENDPOINTS.GetDocBoxDocumentsSet) > 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.GetDocBoxDocumentsSet + '.json';
        } else if (url.indexOf(ENDPOINTS.DocboxUploadSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.DocboxUploadSet + '.json';
        } else if (url.indexOf(ENDPOINTS.NotificationOtherTasks) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.NotificationOtherTasks + '.json';
        } else if (url.indexOf(ENDPOINTS.NotificationCriticalityChange) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.NotificationCriticalityChange + '.json';
        } else if (url.indexOf(ENDPOINTS.ApprovedPCHDuractionChange) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.ApprovedPCHDuractionChange + '.json';
        } else if (url.indexOf(ENDPOINTS.NotificationDurationChange) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.NotificationDurationChange + '.json';
        } else if (url.indexOf(ENDPOINTS.GetTestFormListSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.GetTestFormListSet + '.json';
        } else if (url.indexOf(ENDPOINTS.UserProfileSet) >= 0) {
            const lockValue = loadLocalLock();
            const outObject = {
                key: 'pace-device-lock',
                value: JSON.stringify(lockValue),
                timestamp: 0,
            };
            const mockResponse = {
                d: {
                    results: [
                        {
                            key: 'Z003MZ8X-pace-device-lock',
                            value: EncodingUtilities.base64EncodeUnicode(JSON.stringify(outObject)),
                            gid: 'Z003MZ8X',
                            timestamp: '',
                        },
                    ],
                },
            };
            return of(new HttpResponse({ status: 200, body: mockResponse }));
        } else if (url.indexOf(ENDPOINTS.PIScheduleSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.PIScheduleSet + '.json';
        } else if (url.indexOf(ENDPOINTS.PhysicalInventorySet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.PhysicalInventorySet + '.json';
        } else if (url.indexOf(ENDPOINTS.BootstockMaterialInfo) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.BootstockMaterialInfo + '.json';
        } else if (url.indexOf(ENDPOINTS.HistoricalValuesSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.HistoricalValuesSet + '.json';
        } else if (url.indexOf(ENDPOINTS.MeasurementEquipsSet) >= 0) {
            mockDataUrl = mockdataFolder + ENDPOINTS.MeasurementEquipsSet + '.json';
        } else if (url.indexOf(ENDPOINTS.AppointmentGet) >= 0) {
            mockDataUrl = mockdataFolder + '/AppointmentGet.json';
        } else if (url.indexOf(ENDPOINTS.AppointmentBook) >= 0) {
            mockDataUrl = mockdataFolder + '/AppointmentBook.json';
        } else if (url.indexOf(ENDPOINTS.AzUserStore) >= 0) {
            mockDataUrl = mockdataFolder + '/UserStore.json';
        } else if (url.indexOf(SmartlockerEndpoints.GetSmartLockers) >= 0) {
            mockDataUrl = mockdataFolder + '/GetSmartLockers.json';
        } else if (url.indexOf(SmartlockerEndpoints.GetSmartLockerDoors) >= 0) {
            mockDataUrl = mockdataFolder + '/GetSmartLockerDoors.json';
        } else if (url.indexOf(SmartlockerEndpoints.GetSmartlockerMaterials) >= 0) {
            mockDataUrl = mockdataFolder + '/GetSmartlockerMaterials.json';
        } else if (url.indexOf(SmartlockerEndpoints.GenerateSmartLockerAccessCode) >= 0) {
            mockDataUrl = mockdataFolder + '/GenerateSmartLockerAccessCode.json';
        } else if (url.indexOf(SmartlockerEndpoints.SetMaterialStatus) >= 0) {
            mockDataUrl = mockdataFolder + '/SetMaterialStatus.json';
        } else if (url.indexOf(SmartlockerEndpoints.SearchSmartLockerDeliveries) >= 0) {
            mockDataUrl = mockdataFolder + '/SearchSmartLockerDeliveries.json';
        } else if (url.indexOf('.json') > 0) {
            mockDataUrl = url;
        }

        if (!doNotIntercept && !mockDataUrl) {
            this.log.error('MockModeInterceptorService: intercept: url for mockmode could not be resolved ', url);
            return throwError(
                `MockModeInterceptorService: request: Could not find suitable mockdata json for url '${url}'`,
            );
        }
        const request = doNotIntercept
            ? req.clone()
            : req.clone({
                  method: HTTP_REQUEST_METHOD.GET,
                  url: mockDataUrl,
              });

        return next.handle(request);
    }

    /**
     * In-memory fake of the v1/device-lock API backed by localStorage, mirroring the real
     * backend semantics: acquire is idempotent per device, rejects with 423 + holder info
     * when another device owns the lock, force-acquire always takes over.
     *
     * @param req the intercepted request
     * @returns the mocked response, or null when the request is not a device-lock call
     */
    private interceptDeviceLock(req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> | null {
        const url = req.url;
        if (url.indexOf(ENDPOINTS.DeviceLockGet) < 0) {
            return null;
        }

        const deviceId = req.headers.get('x-pace-device-id') ?? 'mock-device';
        const now = new Date().toISOString();

        // acquire / force-acquire — checked first because DeviceLockGet is a prefix of all routes
        if (url.indexOf(ENDPOINTS.DeviceLockAcquire) >= 0 || url.indexOf(ENDPOINTS.DeviceLockForceAcquire) >= 0) {
            const isForce = url.indexOf(ENDPOINTS.DeviceLockForceAcquire) >= 0;
            const existing = loadLocalLock();
            const heldByOther = !!existing?.fingerprint?.clientId && existing.fingerprint.clientId !== deviceId;

            if (heldByOther && !isForce) {
                return throwError(
                    () =>
                        new HttpErrorResponse({
                            status: 423,
                            statusText: 'Locked',
                            url,
                            error: {
                                message: 'Device lock already held by another device',
                                code: 'DEVICE_LOCK_HELD',
                                lock: {
                                    deviceId: existing.fingerprint?.clientId,
                                    device: {
                                        os: existing.fingerprint?.os,
                                        vendor: existing.fingerprint?.vendor,
                                        model: existing.fingerprint?.model,
                                        browser: existing.fingerprint?.browser,
                                        isPrivateBrowser: existing.fingerprint?.isPrivateBrowser ?? false,
                                    },
                                    acquiredAt: existing.acquiredAt ?? undefined,
                                },
                            },
                        }),
                );
            }

            const body = (req.body ?? {}) as DeviceLockAcquireRequestBody;
            const refreshedOwnLock = existing?.fingerprint?.clientId === deviceId;
            const acquiredLock: LockValue = {
                fingerprint: {
                    clientId: deviceId,
                    os: body.os,
                    vendor: body.vendor,
                    model: body.model,
                    browser: body.browser,
                    isPrivateBrowser: body.isPrivateBrowser ?? false,
                },
                acquiredAt: refreshedOwnLock ? (existing?.acquiredAt ?? now) : now,
            };
            saveLocalLock(acquiredLock);
            return of(
                new HttpResponse({
                    status: refreshedOwnLock ? 200 : 201,
                    body: {
                        acquired: true,
                        deviceId,
                        acquiredAt: acquiredLock.acquiredAt,
                        message: 'Device lock acquired (mock)',
                    },
                }),
            );
        }

        if (url.indexOf(ENDPOINTS.DeviceLockRelease) >= 0) {
            saveLocalLock(EMPTY_DEVICE_LOCK);
            return of(
                new HttpResponse({ status: 200, body: { released: true, message: 'Device lock released (mock)' } }),
            );
        }

        // plain GET v1/device-lock — read-only state
        const lock = loadLocalLock();
        if (lock?.fingerprint?.clientId) {
            return of(
                new HttpResponse({
                    status: 200,
                    body: {
                        locked: true,
                        deviceId: lock.fingerprint.clientId,
                        device: {
                            os: lock.fingerprint.os,
                            vendor: lock.fingerprint.vendor,
                            model: lock.fingerprint.model,
                            browser: lock.fingerprint.browser,
                            isPrivateBrowser: lock.fingerprint.isPrivateBrowser,
                        },
                        acquiredAt: lock.acquiredAt ?? undefined,
                    },
                }),
            );
        }
        return of(new HttpResponse({ status: 200, body: { locked: false } }));
    }
}
